import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { formatUsd, shortId } from '../lib/format';
import { Link } from 'react-router-dom';
import { TestBadge } from '../components/TestBadge';
import { DisputeBadge } from '../components/DisputeBadge';

interface OrdersListResponse {
  orders: Array<{
    uuid: string;
    total: number;
    payment_status: string;
    status: string;
    fulfillment_status: string;
    customer_email: string;
    customer_name: string;
    created_at: string;
    gelato_dispatch_pending: number;
    is_test: number;
    dispute_status: string | null;
  }>;
}

export function Dashboard() {
  const [data, setData] = useState<OrdersListResponse | null>(null);
  const [support, setSupport] = useState<{ open: number; pending: number } | null>(null);

  useEffect(() => {
    api.get<OrdersListResponse>('/api/admin/orders?limit=10').then(setData).catch(console.error);
    api.get<{ open: number; pending: number }>('/api/admin/support/stats').then(setSupport).catch(console.error);
  }, []);

  if (!data) return <p>Loading…</p>;

  // Test orders (Stripe test keys, from preview) are listed but never counted.
  const real = data.orders.filter((o) => o.is_test !== 1);
  const unfulfilled = real.filter(
    (o) => o.status === 'processing' && o.fulfillment_status !== 'fulfilled'
  ).length;
  const pendingGelato = real.filter((o) => o.gelato_dispatch_pending === 1).length;
  const revenue = real.reduce((s, o) => s + o.total, 0);

  return (
    <div>
      <h1 style={{ fontFamily: 'Georgia, serif', fontSize: 28, fontWeight: 400 }}>Dashboard</h1>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 16,
          marginTop: 24,
          marginBottom: 32,
        }}
      >
        <Stat label="Recent revenue" value={formatUsd(revenue)} />
        <Stat label="Awaiting fulfillment" value={String(unfulfilled)} />
        <Stat label="Gelato retries needed" value={String(pendingGelato)} highlight={pendingGelato > 0} />
        <Link to="/support" style={{ textDecoration: 'none', color: 'inherit' }}>
          <Stat
            label="Open support cases"
            value={String(support?.open ?? '—')}
            highlight={(support?.open ?? 0) > 0}
          />
        </Link>
      </div>

      <h2 style={{ fontSize: 14, letterSpacing: 2, textTransform: 'uppercase', color: '#6b7280' }}>
        Recent Orders
      </h2>
      <div style={{ background: '#fff', border: '1px solid #e5e5e5', borderRadius: 8, overflow: 'hidden' }}>
        {data.orders.length === 0 && (
          <p style={{ padding: 24, color: '#6b7280', margin: 0 }}>No orders yet.</p>
        )}
        {data.orders.map((o) => (
          <Link
            key={o.uuid}
            to={`/orders/${o.uuid}`}
            style={{
              display: 'grid',
              gridTemplateColumns: '120px 1fr 120px 120px',
              gap: 16,
              padding: '14px 20px',
              borderBottom: '1px solid #f0f0f0',
              alignItems: 'center',
            }}
          >
            <span style={{ fontFamily: 'monospace', fontSize: 13 }}>
              #{shortId(o.uuid)}
              {o.is_test === 1 && <TestBadge />}
              {o.dispute_status && <DisputeBadge status={o.dispute_status} />}
            </span>
            <span style={{ fontSize: 14 }}>{o.customer_email}</span>
            <span style={{ fontSize: 13, color: '#6b7280' }}>{o.status}</span>
            <span style={{ fontSize: 14, textAlign: 'right' }}>{formatUsd(o.total)}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div
      style={{
        background: '#fff',
        border: `1px solid ${highlight ? '#b91c1c' : '#e5e5e5'}`,
        borderRadius: 8,
        padding: 20,
      }}
    >
      <div style={{ fontSize: 12, color: '#6b7280', letterSpacing: 1, textTransform: 'uppercase' }}>
        {label}
      </div>
      <div style={{ fontSize: 24, fontFamily: 'Georgia, serif', marginTop: 6 }}>{value}</div>
    </div>
  );
}
